import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { lightThemeClass, darkThemeClass } from './themes.css';

export type ColorScheme = 'light' | 'dark' | 'system';
export type ResolvedColorScheme = 'light' | 'dark';

/**
 * Persistence seam for the chosen colour scheme — mirrors the RN
 * `ColorSchemeStorage`. The design system stays storage-free; the app injects an
 * adapter (e.g. localStorage-backed) so the choice survives reloads.
 */
export interface ColorSchemeStorage {
    get(): ColorScheme | null;
    set(scheme: ColorScheme): void;
}

interface ThemeContextValue {
    colorScheme: ColorScheme;
    resolvedColorScheme: ResolvedColorScheme;
    setColorScheme: (scheme: ColorScheme) => void;
    /** The vanilla-extract class to apply for the resolved scheme. */
    themeClass: string;
}

const ThemeContext = createContext<ThemeContextValue>({
    colorScheme: 'system',
    resolvedColorScheme: 'dark',
    setColorScheme: () => undefined,
    themeClass: darkThemeClass,
});

function prefersDark(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return true; // dark-first
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

interface ThemeProviderProps {
    children: ReactNode;
    /** Dark-first: the default when the user has expressed no preference. */
    defaultColorScheme?: ColorScheme;
    storage?: ColorSchemeStorage;
}

/**
 * Web mirror of the RN `ThemeProvider`. Applies the resolved theme class to a
 * full-height wrapper so the whole tree picks up the right token values. Dark is
 * the app default (and the `:root` global), so a `system` user with no OS
 * preference still lands on dark.
 */
export function ThemeProvider({
    children,
    defaultColorScheme = 'system',
    storage,
}: ThemeProviderProps) {
    const [colorScheme, setColorSchemeState] = useState<ColorScheme>(
        () => storage?.get() ?? defaultColorScheme,
    );
    const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => setSystemDark(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const setColorScheme = useCallback(
        (scheme: ColorScheme) => {
            storage?.set(scheme);
            setColorSchemeState(scheme);
        },
        [storage],
    );

    const resolvedColorScheme: ResolvedColorScheme =
        colorScheme === 'system' ? (systemDark ? 'dark' : 'light') : colorScheme;

    const themeClass = resolvedColorScheme === 'dark' ? darkThemeClass : lightThemeClass;

    const value = useMemo<ThemeContextValue>(
        () => ({ colorScheme, resolvedColorScheme, setColorScheme, themeClass }),
        [colorScheme, resolvedColorScheme, setColorScheme, themeClass],
    );

    return (
        <ThemeContext.Provider value={value}>
            <div className={themeClass} style={{ height: '100%' }} data-color-scheme={resolvedColorScheme}>
                {children}
            </div>
        </ThemeContext.Provider>
    );
}

export function useTheme(): ThemeContextValue {
    return useContext(ThemeContext);
}
